// store/forms/delForm/sdsReferences/delFormSdsReferencesActions.js
// SDS references attached to a DEL from the 3E catalog (Misc > SDS). Rows are immutable: add/remove only.

import { DelSdsReference } from "../../../../models/Del/DelSdsReference";

export const makeSdsReferencesActions = ({ getDraft, bump }) => ({
  // Optimistic add; a direct server rejection (duplicate, validation) drops the row again.
  // A patch rejected while queued has no error callback, so the next DEL fetch reconciles it.
  attachSdsReference(hit, addedBy, emitPatch) {
    const draft = getDraft();
    if (draft.hasSdsReference(hit?.product_uid)) {
      return { ok: false, duplicate: true };
    }
    const reference = DelSdsReference.fromSearchHit(hit, addedBy);
    draft.addSdsReference(reference);
    bump();
    if (typeof emitPatch === "function") {
      emitPatch(reference.toPatch("add"), {
        onError: () => {
          draft.removeSdsReference(reference.rowId);
          bump();
        },
      });
    }
    return { ok: true, rowId: reference.rowId };
  },

  // Emit first, then splice (client-signatures idiom)
  removeSdsReference(rowId, emitPatch) {
    const draft = getDraft();
    const reference =
      (draft.sdsReferences || []).find((r) => r.rowId === rowId) || null;
    if (!reference) return false;
    if (typeof emitPatch === "function") {
      emitPatch(reference.toPatch("remove"));
    }
    draft.removeSdsReference(rowId);
    bump();
    return true;
  },

  // Broadcast rows always become DelSdsReference instances, never plain objects
  handleBroadcast(patch) {
    const draft = getDraft();
    if (!draft || !patch) return;
    const { id, action } = patch;

    if (action === "remove") {
      const idx = (draft.sdsReferences || []).findIndex((r) => r.rowId === id);
      if (idx >= 0) {
        draft.sdsReferences.splice(idx, 1);
        bump();
      }
      return;
    }

    // add: the sender already holds its optimistic row under this id
    const rows = draft.sdsReferences || [];
    if (rows.some((r) => r.rowId === id)) return;
    const incoming = new DelSdsReference(patch);
    // Same product under another id: the other device's add won the race, so the server row replaces
    // our optimistic one and the later duplicate rejection (onError) finds nothing left to drop
    const sameProductIdx = incoming.productUid
      ? rows.findIndex((r) => r.productUid === incoming.productUid)
      : -1;
    if (sameProductIdx >= 0) {
      draft.sdsReferences = rows.map((r, idx) => (idx === sameProductIdx ? incoming : r));
    } else {
      draft.addSdsReference(incoming);
    }
    bump();
  },
});
